import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { Bell, CheckCircle, Sun, Moon, Monitor, Heart, MessageCircle, Send, Calendar, Users } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { DEFAULT_NOTIFICATION_PREFS } from '../../utils/constants';

const SettingsView = () => {
  const { currentUser, setCurrentUser, users, setUsers, theme, effectiveTheme, setTheme, setView } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [pushPermission, setPushPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );

  const prefs = currentUser.notificationPreferences || DEFAULT_NOTIFICATION_PREFS;

  const requestPushPermission = async () => {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission === 'granted') return true;
    const result = await Notification.requestPermission();
    setPushPermission(result);
    return result === 'granted';
  };

  const togglePreference = async (category) => {
    const turningOn = prefs[category] === false;
    if (turningOn && pushPermission !== 'granted' && pushPermission !== 'unsupported') {
      const granted = await requestPushPermission();
      if (!granted) return;
    }
    const newPrefs = { ...DEFAULT_NOTIFICATION_PREFS, ...prefs, [category]: !prefs[category] };
    setSaving(true);
    try {
      await firestoreService.updateUser(currentUser.id, { notificationPreferences: newPrefs });
      const updatedUser = { ...currentUser, notificationPreferences: newPrefs };
      setCurrentUser(updatedUser);
      setUsers(prev => prev.map(u => u.id === currentUser.id ? updatedUser : u));
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    } catch (err) {
      console.error('Error saving notification preferences:', err);
    }
    setSaving(false);
  };

  const enableAll = async () => {
    if (pushPermission !== 'granted' && pushPermission !== 'unsupported') {
      const granted = await requestPushPermission();
      if (!granted) return;
    }
    const newPrefs = { likes: true, comments: true, messages: true, events: true, groups: true };
    setSaving(true);
    try {
      await firestoreService.updateUser(currentUser.id, { notificationPreferences: newPrefs });
      const updatedUser = { ...currentUser, notificationPreferences: newPrefs };
      setCurrentUser(updatedUser);
      setUsers(prev => prev.map(u => u.id === currentUser.id ? updatedUser : u));
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    } catch (err) {
      console.error('Error saving notification preferences:', err);
    }
    setSaving(false);
  };

  const notificationCategories = [
    { key: 'likes', label: 'Social Interactions', description: 'Likes, reactions, follows, comment likes', icon: Heart },
    { key: 'comments', label: 'Post Replies', description: 'Comments on your posts', icon: MessageCircle },
    { key: 'messages', label: 'Messages & Shares', description: 'Posts and events shared with you', icon: Send },
    { key: 'events', label: 'Event Notifications', description: 'Event invites and joins', icon: Calendar },
    { key: 'groups', label: 'Group Notifications', description: 'Group invites and join requests', icon: Users },
  ];

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Light backgrounds with dark text' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Dark backgrounds with light text' },
    { value: 'system', label: 'System Default', icon: Monitor, description: `Follows your system's setting (currently ${effectiveTheme})` },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button onClick={() => setView('feed')} className="text-[#16a34a] hover:underline mb-4 inline-block">&larr; Back</button>
      <h2 className="text-2xl font-bold text-white mb-6">Settings</h2>

      {/* Notifications Section */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-white" />
            <h3 className="text-lg font-bold text-white">Push Notifications</h3>
          </div>
          <button onClick={enableAll} disabled={saving} className="text-xs px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50">
            Enable All
          </button>
        </div>
        {pushPermission === 'denied' && (
          <div className="rounded-lg p-3 mb-3" style={{ backgroundColor: 'rgb(var(--color-warning-red-bg) / 0.3)', borderWidth: '1px', borderColor: 'rgb(var(--color-warning-red-border))' }}>
            <p className="text-sm" style={{ color: 'rgb(var(--color-warning-red))' }}>Push notifications are blocked by your browser. To enable them, update your browser's notification settings for this site.</p>
          </div>
        )}
        {pushPermission === 'unsupported' && (
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3 mb-3">
            <p className="text-yellow-300 text-sm">Your browser does not support push notifications.</p>
          </div>
        )}
        <div className="space-y-3">
          {notificationCategories.map(cat => {
            const Icon = cat.icon;
            const enabled = prefs[cat.key] !== false;
            return (
              <div key={cat.key} className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm">{cat.label}</p>
                    <p className="text-gray-400 text-xs">{cat.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => togglePreference(cat.key)}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${enabled ? 'bg-[#16a34a]' : 'bg-gray-600'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Appearance Section */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Sun className="w-5 h-5 text-white" />
          <h3 className="text-lg font-bold text-white">Appearance</h3>
        </div>
        <div className="space-y-2">
          {themeOptions.map((opt) => {
            const Icon = opt.icon;
            const isSelected = theme === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg border-2 transition-colors ${
                  isSelected ? 'border-[#16a34a] bg-gray-700' : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <Icon className={`w-5 h-5 ${isSelected ? 'text-[#16a34a]' : 'text-gray-400'}`} />
                <div className="text-left flex-1">
                  <p className="text-white font-medium">{opt.label}</p>
                  <p className="text-gray-400 text-sm">{opt.description}</p>
                </div>
                {isSelected && <CheckCircle className="w-5 h-5 text-[#16a34a]" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};


export default SettingsView;
